import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * Empty state component for when an exam is not available or accessible
 * @param {Object} props
 * @param {string} props.title - Main title for the empty state
 * @param {string} props.message - Descriptive message
 * @param {string} props.redirectPath - Path to redirect when button is clicked
 * @param {string} props.buttonMessage - Path to redirect when button is clicked
 */
const EmptyState = ({ title, message, redirectPath, buttonMessage }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleBackToExams = () => {
    navigate(redirectPath);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      textAlign="center"
      sx={{
        padding: theme.spacing(4),
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack spacing={3} alignItems="center" maxWidth="500px">
        <ErrorOutlineIcon
          sx={{
            fontSize: 80,
            color: theme.palette.text.secondary,
            opacity: 0.6,
          }}
        />

        <Typography
          variant="h5"
          component="h2"
          color="text.primary"
          sx={{ fontWeight: 500 }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            maxWidth: "400px",
          }}
        >
          {message}
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={handleBackToExams}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            borderRadius: theme.spacing(1),
            paddingX: theme.spacing(4),
            paddingY: theme.spacing(1.5),
            marginTop: theme.spacing(2),
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {buttonMessage}
        </Button>
      </Stack>
    </Box>
  );
};

EmptyState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  redirectPath: PropTypes.string,
  buttonMessage: PropTypes.string,
};

export default EmptyState;
